// src/ui/components/dialogs/FullscreenDialog.media.tsx
import React from 'react';
import parse from 'html-react-parser';
import { MediaRendererProps } from './FullscreenDialog.types';
import { optimizeYouTubeUrl } from './FullscreenDialog.utils';
import { useDeviceDetection } from '../../../core/utils/deviceDetection';

/**
 * Renderiza el media en modo fullscreen (solo el primer elemento)
 */
export const RenderFullscreenMedia: React.FC<MediaRendererProps> = ({
  html,
  parserOptions,
  shouldLoadMedia,
  title,
}) => {
  const { isRaspberryPi } = useDeviceDetection();
  
  if (!html || typeof window === 'undefined') return null;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let el = doc.body.firstElementChild;

    // Si el primer elemento no es media, buscar dentro
    if (el && !['IFRAME', 'IMG', 'VIDEO'].includes(el.tagName)) {
      const found = el.querySelector('iframe,video,img');
      if (found) el = found as Element;
    }

    if (!el) {
      // fallback: usa parse normal
      return (
        <div className="w-full h-screen flex items-center justify-center">
          {parse(html, parserOptions)}
        </div>
      );
    }

    const tag = el.tagName;

    if (tag === 'IFRAME') {
      const src = (el.getAttribute('src') || el.getAttribute('data-src') || '').toString();

      if (!shouldLoadMedia) {
        return (
          <div className="w-full h-screen flex items-center justify-center bg-gray-900">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p>Preparando video...</p>
            </div>
          </div>
        );
      }

      // Aplicar optimizaciones específicas para Raspberry Pi
      const optimizedSrc = optimizeYouTubeUrl(src, isRaspberryPi);

      return (
        <iframe
          src={optimizedSrc}
          title="media-fullscreen"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; loop"
          allowFullScreen
          loading="eager" // Cargar inmediatamente, no lazy loading (mejor para Raspberry Pi)
          onError={(e) => {
            console.warn('YouTube iframe error:', e);
            if (e.currentTarget.src !== src) {
              e.currentTarget.src = src;
            }
          }}
          onLoad={() => {
            console.log('YouTube iframe loaded successfully');
          }}
          className="w-full h-screen max-h-screen"
          style={{ 
            width: '100%', 
            height: '100vh',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        />
      );
    }

    if (tag === 'VIDEO') {
      const src = (el.getAttribute('src') || el.getAttribute('data-src') || '').toString();
      const poster = el.getAttribute('poster') || undefined;
      return (
        <video
          src={src}
          poster={poster}
          controls
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-screen object-cover max-h-screen"
          style={{ 
            width: '100%', 
            height: '100vh',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        />
      );
    }

    if (tag === 'IMG') {
      const src = (el.getAttribute('src') || el.getAttribute('data-src') || '').toString();
      return (
        <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
          <img
            src={src}
            alt=""
            className="w-full h-full object-cover"
            style={{ width: '100%', height: '100vh', objectFit: 'cover' }}
          />
          <div
            style={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10000,
              color: '#fff',
              fontWeight: 900,
              fontSize: '24px',
              background: 'rgba(0,0,0,0.45)',
              padding: '8px 12px',
              borderRadius: 8,
              textAlign: 'center',
              maxWidth: '80%',
            }}
          >
            {title}
          </div>
        </div>
      );
    }

    // fallback
    return (
      <div className="w-full h-screen flex items-center justify-center">
        {parse(html, parserOptions)}
      </div>
    );
  } catch (e) {
    // fallback seguro
    return (
      <div className="w-full h-screen flex items-center justify-center">
        {parse(html, parserOptions)}
      </div>
    );
  }
};

